import TokenRepository from '@/repositories/TokenRepository';
import { defineStore } from 'pinia';

const STALE_AFTER_MS = 60_000;
const OPTIONS_STALE_AFTER_MS = 60_000;

function checkoutUrlFromResponse(response) {
  return response?.data?.url || response?.data?.checkout_url || response?.data;
}

export const useTokenBalanceStore = defineStore('tokenBalance', {
  state: () => ({
    account: null,
    loading: false,
    error: null,
    lastLoadedAt: 0,
    topUpOptions: null,
    topUpOptionsLoading: false,
    topUpOptionsError: null,
    topUpOptionsLoadedAt: 0,
    autoRechargeSaving: false,
  }),

  getters: {
    totalBalance: (state) => Number(state.account?.total_balance ?? 0),
    includedBalance: (state) => Number(state.account?.included_balance ?? 0),
    promotionalBalance: (state) => Number(state.account?.promotional_balance ?? 0),
    purchasedBalance: (state) => Number(state.account?.purchased_balance ?? 0),
    hardLimit: (state) => state.account?.hard_limit ?? null,
    softLimit: (state) => state.account?.soft_limit ?? null,
    isLowBalance: (state) => Number(state.account?.total_balance ?? 0) <= Number(state.account?.soft_limit ?? 0),
    includedAllowance: (state) => Number(state.account?.included_allowance ?? 0),
    planKeys: (state) => (Array.isArray(state.account?.plan_keys) ? state.account.plan_keys : []),
    periodEndsAt: (state) => state.account?.period_ends_at ?? null,
    subscriptionStatus: (state) => state.account?.subscription_status ?? 'none',
    hasSubscription: (state) => (state.account?.subscription_status ?? 'none') !== 'none',
    topUpPacks: (state) => (Array.isArray(state.topUpOptions?.packs) ? state.topUpOptions.packs : []),
    autoRechargeSettings: (state) => state.topUpOptions?.auto_recharge ?? null,
    defaultTopUpPackKey: (state) => state.topUpOptions?.defaults?.pack_key ?? 'topup-1000',
  },

  actions: {
    async refresh({ force = false } = {}) {
      const freshEnough = this.lastLoadedAt && Date.now() - this.lastLoadedAt < STALE_AFTER_MS;
      if (!force && freshEnough) {
        return this.account;
      }

      this.loading = true;
      this.error = null;
      try {
        const response = await TokenRepository.balance();
        this.account = response.data;
        this.lastLoadedAt = Date.now();
        return this.account;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    clear() {
      this.account = null;
      this.error = null;
      this.lastLoadedAt = 0;
      this.loading = false;
      this.topUpOptions = null;
      this.topUpOptionsError = null;
      this.topUpOptionsLoadedAt = 0;
      this.topUpOptionsLoading = false;
      this.autoRechargeSaving = false;
    },

    async loadTopUpOptions({ force = false } = {}) {
      const freshEnough = this.topUpOptionsLoadedAt && Date.now() - this.topUpOptionsLoadedAt < OPTIONS_STALE_AFTER_MS;
      if (!force && freshEnough) {
        return this.topUpOptions;
      }

      this.topUpOptionsLoading = true;
      this.topUpOptionsError = null;
      try {
        const response = await TokenRepository.topUpOptions();
        this.topUpOptions = response.data;
        this.topUpOptionsLoadedAt = Date.now();
        return this.topUpOptions;
      } catch (error) {
        this.topUpOptionsError = error;
        throw error;
      } finally {
        this.topUpOptionsLoading = false;
      }
    },

    async createTopUpCheckout(packKey) {
      const response = await TokenRepository.topUpCheckout(packKey);
      const checkoutUrl = checkoutUrlFromResponse(response);
      if (typeof checkoutUrl === 'string' && checkoutUrl.length > 0) {
        return { url: checkoutUrl };
      }

      throw new Error('Token top-up checkout is not configured.');
    },

    async startTopUp(packKey) {
      const checkout = await this.createTopUpCheckout(packKey);
      window.open(checkout.url, '_blank', 'noopener,noreferrer');
      return checkout;
    },

    async updateAutoRecharge(payload) {
      this.autoRechargeSaving = true;
      try {
        const response = await TokenRepository.updateAutoRecharge(payload);
        this.topUpOptions = response.data;
        this.topUpOptionsLoadedAt = Date.now();
        return this.topUpOptions;
      } finally {
        this.autoRechargeSaving = false;
      }
    },

    async openBillingPortal() {
      const response = await TokenRepository.billingPortal();
      const url = checkoutUrlFromResponse(response);
      if (typeof url === 'string' && url.length > 0) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return { url };
      }

      throw new Error('Billing portal is not configured.');
    },
  },
});
